/* eslint-disable */
import React, { useState, useEffect, useMemo, useRef } from 'react';

// Draggable Scroll
import { useDraggable } from 'react-use-draggable-scroll';

// Redux
import { useSelector } from 'react-redux';

// Material
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

// Immer
import produce from 'immer';

// Types
import { ApplicationState } from 'store';

// Components
import {
  DialogueExport,
  XlsHeaderInterface,
  XlsRowInterface,
} from 'components';

// Helpers
import useWindowDimensions from 'helpers/useWindowDimensions';

// Styles
import useStyles from './TableSegmentQuantityReport.styles';

interface TableLineDemandInterface {
  setTableCSV: (arg: any) => void;
  csvRef?: any;
  tableReference?: any;
  periodString?: string;
}

type Props = TableLineDemandInterface;

const DRAWER_WIDTH = 300;
const BORDER_AND_SPACINGS = 80;

const KPIS_DISPLAY = {
  ipk: 'IPK',
  ipke: 'IPKE',
  valuePerKm: 'R$/Km',
  km: 'KM',
  idleKm: 'KM Ocioso',
  trips: 'Viagens',
};

const TableSegmentQuantityReport: React.FC<Props> = ({
  setTableCSV,
  csvRef,
  tableReference,
  periodString,
}) => {
  const classes = useStyles();
  const parentRef: any = useRef();
  const { events } = useDraggable(parentRef);

  const { width: windowWidth } = useWindowDimensions();

  const drawerOpen = useSelector(
    (state: ApplicationState) => state.drawer.open,
  );

  const [xlsHeader, setXlsHeader] = useState<XlsHeaderInterface[]>();
  const [xlsRows, setXlsRows] = useState<Array<XlsRowInterface[]>>([]);
  const [benefitsList, setBenefitsList] = useState<string[]>([]);

  const [simplePdfData, setSimplePdfData] = useState<{
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }>({
    title: '',
    subtitle: '',
    filters: '',
    headers: [],
    rows: [],
  });

  const reports = useSelector(
    (state: ApplicationState) =>
      state.reports.dailyQuantitySeccionedReportList || null,
  );

  useEffect(() => {
    setBenefitsList(
      produce(benefitsList, () => {
        const benefitsList: string[] = [];

        if (reports) {
          for (const item in reports) {
            const { benefitsCount = {} } = reports[item];
            for (const benefit of Object.keys(benefitsCount)) {
              if (!benefitsList.includes(benefit)) {
                benefitsList.push(benefit);
              }
            }
          }
        }
        return benefitsList.sort();
      }),
    );
  }, [reports]);
  const width = useMemo(() => {
    if (drawerOpen) {
      return windowWidth - (DRAWER_WIDTH + BORDER_AND_SPACINGS);
    }
    return windowWidth - BORDER_AND_SPACINGS * 2;
  }, [windowWidth, drawerOpen]);

  const getBenefitCount = useMemo(
    () => (benefit: string) => {
      let total = 0;
      for (const entry of Object.values(reports)) {
        // @ts-ignore
        if (
          entry.benefitsCount.hasOwnProperty(benefit) &&
          entry.benefitsCount[benefit as keyof typeof entry.benefitsCount]
        ) {
          // @ts-ignore
          total += entry.benefitsCount[benefit];
        }
      }
      return total;
    },
    [reports],
  );

  const getLineBenefitCount = useMemo(
    () => (benefitsCount: object, benefit: string) => {
      // @ts-ignore
      if (benefitsCount.hasOwnProperty(benefit)) return benefitsCount[benefit];
      else return 0;
    },
    [reports],
  );

  const getBenefitCountValue = useMemo(
    () => (benefitsCount: object) => {
      return Object.entries(benefitsCount).reduce(
        // @ts-ignore
        (total, [_, value]) => (total += Number(value) ? value : 0),
        0,
      );
    },
    [],
  );

  const getBenefitCountTotalValue = useMemo(() => {
    let total = 0;
    for (const entry of Object.values(reports)) {
      for (const benefit of Object.keys(entry.benefitsCount)) {
        // @ts-ignore
        if (entry.benefitsCount[benefit]) {
          // @ts-ignore
          total += entry.benefitsCount[benefit];
        }
      }
    }
    return total;
  }, [reports]);

  // generating the table csv
  useEffect(() => {
    let csv = '';
    let header = 'Embarques;';

    if (benefitsList) {
      Object.keys(reports)
        .sort()
        .forEach((key, i, arr) => {
          header += `${key};`;
          if (arr.length === i + 1) header += `Total;\n`;
        });

      csv += header;

      csv += benefitsList
        .map((benefit) => {
          let contentAux1 = '';

          Object.entries(reports)
            .sort()
            .forEach(([key, { benefits, benefitsCount }]) => {
              contentAux1 =
                contentAux1 +
                `${getLineBenefitCount(benefitsCount, benefit) || 0};`;
            });

          return `${benefit};${contentAux1}${getBenefitCount(benefit) || 0};\n`;
        })
        .join('');

      let contentTotal = 'Total;';
      Object.entries(reports)
        ?.sort()
        ?.map(([key, { benefits, benefitsCount }]) => {
          contentTotal += `${getBenefitCountValue(benefitsCount)};`;
        });
      csv += contentTotal + `${getBenefitCountTotalValue}`;
      setTableCSV(csv);
    }

    setTableCSV(csv);
  }, [benefitsList, setTableCSV]);

  useEffect(() => {
    const firstColumn = ['Embarques'];
    const dynamicHeader = Object.keys(reports)
      ?.sort()
      ?.map((key) => key);
    const lastColumn = ['Total'];

    const headerSimplePdf = firstColumn
      .concat(dynamicHeader)
      .concat(lastColumn);

    const profileLabels =
      benefitsList?.length > 0
        ? benefitsList.map((benefit: string) => benefit)
        : [];

    const profileRows =
      benefitsList?.length > 0
        ? benefitsList.map((benefit: string) =>
            Object.entries(reports)
              .sort()
              .map(([key, { benefits, benefitsCount }]) => {
                const benefitValue =
                  getLineBenefitCount(benefitsCount, benefit) || 0;

                return `${benefitValue} `;
              })
              .concat([`${getBenefitCount(benefit)}`]),
          )
        : [];

    const lastRow = Object.entries(reports)
      .sort()
      .map(
        ([key, { benefits, benefitsCount }], i) =>
          `${getBenefitCountValue(benefitsCount)}`,
      )
      .concat([`${getBenefitCountTotalValue}`]);

    const combinedProfilesArray = profileLabels.map((label, index) => {
      return [label, ...profileRows[index]];
    });

    const combinedProfilesArrayWithTotal = [
      ...combinedProfilesArray,
      ['Total'].concat(lastRow),
    ];

    const tempSimplePdfData = {
      title: 'ONBOARD',
      subtitle: 'Relatório de Quantidade por Trecho',
      filters: periodString || '',
      headers: headerSimplePdf,
      rows: combinedProfilesArrayWithTotal,
    };

    setSimplePdfData(tempSimplePdfData);
  }, [reports, benefitsList, periodString]);

  useEffect(() => {
    const dynamicXlsHeader = Object.keys(reports)
      ?.sort()
      ?.map((key) => ({
        header: key,
        key: key,
        width: 20,
      }));

    setXlsHeader([
      {
        header: 'Embarques',
        key: 'labels',
        width: 20,
      },
      ...dynamicXlsHeader,
      {
        header: 'Total',
        key: 'totalValue',
        width: 20,
      },
    ]);

    const firstProfileRow = [
      { key: 'labels', value: 'Embarques', image: null },
      ...Object.keys(reports)
        .sort()
        .map((kpi: string, i) => ({ key: kpi, value: '', image: null })),
      { key: 'totalValue', value: '', image: null },
    ];

    const profileRows = benefitsList.map((benefit) => [
      {
        key: 'labels',
        value: `${benefit}`,
        image: null,
      },
      ...Object.entries(reports)
        .sort()
        .map(([key, { benefits, benefitsCount }], i) => ({
          key: Object.keys(reports).sort()[i],
          value: `${getLineBenefitCount(benefitsCount, benefit) || 0}`,
          image: null,
        })),
      {
        key: 'totalValue',
        value: `${getBenefitCount(benefit) || 0}`,
        image: null,
      },
    ]);

    const combinedProfilesArray = [firstProfileRow, ...profileRows];
    const lastRow = [
      {
        key: 'labels',
        value: `Total`,
        image: null,
      },
      ...Object.entries(reports)
        .sort()
        .map(([key, { benefits, benefitsCount }], i) => ({
          key: Object.keys(reports).sort()[i],
          value: `${getBenefitCountValue(benefitsCount)}`,
          image: null,
        })),
      {
        key: 'totalValue',
        value: `${getBenefitCountTotalValue}`,
        image: null,
      },
    ];

    setXlsRows([...combinedProfilesArray, lastRow] as XlsRowInterface[][]);
  }, [reports, benefitsList]);

  return (
    <>
      <Box m={2}>
        <Grid container justifyContent='flex-end' alignItems='center'>
          <Box paddingRight='16px'>
            <DialogueExport
              isDialogPrint
              tableInstance={tableReference}
              csvRef={csvRef}
              beforePrint={() => (document.body.style.zoom = '45%')}
              simplePdf
              simplePdfData={simplePdfData}
              columnsPerPage={5}
              pdfName='quantidade_por_trecho'
              xlsTitle='Relatório de Quantidade por trecho'
              xlsHeader={xlsHeader}
              xlsRows={xlsRows}
              pdf
              csv
              xls
            />
          </Box>
        </Grid>

        <TableContainer
          className={classes.tableContainer}
          style={{ overflow: 'auto', width }}
          {...events}
          ref={parentRef}>
          <Table
            className={classes.table}
            aria-label='demanda por carro'
            style={{ overflow: 'auto', width }}>
            <TableHead>
              <TableRow>
                <TableCell align='left' variant='head'>
                  Embarques
                </TableCell>
                {reports &&
                  Object.keys(reports)
                    .sort()
                    .map((key) => (
                      <TableCell key={key} align='left' variant='head'>
                        {key}
                      </TableCell>
                    ))}
                <TableCell align='left' variant='head'>
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <b>Embarques</b>
                </TableCell>
                {Object.entries(reports)
                  .sort()
                  .map(() => (
                    <TableCell></TableCell>
                  ))}
                <TableCell></TableCell>
              </TableRow>

              {Array.isArray(benefitsList) &&
                benefitsList.map((benefit) => (
                  <TableRow key={benefit}>
                    <TableCell>{benefit}</TableCell>
                    {Object.entries(reports)
                      .sort()
                      .map(([key, { benefits, benefitsCount }]) => (
                        <TableCell key={key}>
                          {getLineBenefitCount(benefitsCount, benefit) || 0}
                        </TableCell>
                      ))}
                    <TableCell>{getBenefitCount(benefit) || 0}</TableCell>
                  </TableRow>
                ))}

              <TableRow>
                <TableCell>
                  <b>Total</b>
                </TableCell>
                {Object.entries(reports)
                  .sort()
                  .map(([key, { benefits, benefitsCount }]) => (
                    <TableCell key={key}>
                      <b>{getBenefitCountValue(benefitsCount)}</b>
                    </TableCell>
                  ))}
                <TableCell>{getBenefitCountTotalValue}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default TableSegmentQuantityReport;
export { TableSegmentQuantityReport };
